import styles from './styles.module.scss';

import { useContext, useEffect, useState } from 'react';
import TrilogicIconLogin from '../../../../assets/images/icons/trilogic-icon-login.png';
import { NavLink, useNavigate } from 'react-router-dom';
import { LoginBox } from '../../../../components/LoginBox';
import { MessageContext } from '../../../../providers/MessageProvider';
import { register } from './requestHandlers';
import { Error, User } from '../../../../types';
import { saveSessionCredentials, verifyRoutePermissions } from '../../../../globalFunctions';
import { LoadingContext } from '../../../../providers/LoadingProvider';
import TrilogicPolicies from '../../../../assets/files/trilogic_termo_de_uso_e_politica_de_privacidade_v2.pdf';

export const SignUpRoute = () => {
    const { setOpenMessage, setMessage } = useContext(MessageContext);
    const { setIsLoadingContent } = useContext(LoadingContext);
    const navigate = useNavigate();

    const [openRegisterBox, setOpenRegisterBox] = useState<boolean>(true);

    // Input fields
    const [image, setImage] = useState<File>();
    const [previewImageURL, setPreviewImageURL] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [surname, setSurname] = useState<string>('');
    const [gender, setGender] = useState<string>();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordType, setPasswordType] = useState<'password' | 'text'>('password');
    const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
    const [passwordConfirmationType, setPasswordConfirmationType] = useState<'password' | 'text'>(
        'password'
    );
    const [agreedLgpd, setAgreedLgpd] = useState<'0' | '1'>('0');

    const validateRegistrationForm = () => {
        let message = '';

        if (name.trim() === '') {
            message = 'Preencha o campo de nome';
        } else if (surname.trim() === '') {
            message = 'Preencha o campo de sobrenome';
        } else if (gender === undefined) {
            message = 'Preencha o campo de gênero';
        } else if (email.trim() === '') {
            message = 'Preencha o campo de email';
        } else if (password.trim() === '') {
            message = 'Preecha o campo de senha';
        } else if (password.trim() !== passwordConfirmation.trim()) {
            message = 'As senhas informadas não são iguais';
        } else if (image === undefined) {
            message = 'Selecione uma imagem de perfil';
        } else if (agreedLgpd === '0') {
            message = 'Você deve aceitar os termos de uso e privacidade para prosseguir';
        }

        if (message === '') {
            return true;
        } else {
            setMessage({
                type: 'warning',
                content: message
            });
            setOpenMessage(true);

            return false;
        }
    };

    const handleRegistration = async (event: React.ChangeEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (validateRegistrationForm()) {
            setIsLoadingContent(true);

            try {
                const result = await register({
                    login_type: 'email',
                    image: image ?? ({} as File),
                    name: name,
                    surname: surname,
                    gender: gender ?? '',
                    email: email,
                    password: password,
                    password_confirmation: passwordConfirmation,
                    lgpd_agreed: agreedLgpd
                });

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    // Save session
                    saveSessionCredentials(result as User);
                    // Navigate to email verification
                    navigate('/auth/email-verification?send=true');
                } else {
                    throw result.error as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content:
                        error.response.status === 409
                            ? 'Este e-mail já foi cadastrado na plataforma'
                            : error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const selectedImage = event.target.files[0];
            setImage(selectedImage);

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewImageURL(reader.result as string);
            };
            reader.readAsDataURL(selectedImage);
        }
    };

    const handleAgreedLgpd = () => {
        if (agreedLgpd === '0') {
            setAgreedLgpd('1');
        } else {
            setAgreedLgpd('0');
        }
    };

    const togglePassword = () => {
        if (passwordType === 'password') {
            setPasswordType('text');
        } else {
            setPasswordType('password');
        }
    };

    const togglePasswordConfirmation = () => {
        if (passwordConfirmationType === 'password') {
            setPasswordConfirmationType('text');
        } else {
            setPasswordConfirmationType('password');
        }
    };

    const showPolicies = () => {
        window.open(TrilogicPolicies);
    };

    useEffect(() => {
        verifyRoutePermissions(['not.auth'], navigate);
    }, []);

    return (
        <div className={styles.wrapper}>
            <div className={styles.mainLeft}></div>

            <div className={styles.mainRight}>
                <img src={TrilogicIconLogin} alt='Logo do Trilogic' />
            </div>

            <LoginBox open={openRegisterBox}>
                <div className={`${styles.loginOption} ${styles.credentials}`}>
                    <div className={`${styles.optionHeader} ${styles.centralize}`}>Cadastro</div>

                    <form onSubmit={handleRegistration} encType='multipart/form-data'>
                        <div className={styles.groupField}>
                            <label className={styles.required} htmlFor='name'>
                                Nome
                            </label>
                            <input
                                type='text'
                                id='name'
                                name='name'
                                value={name}
                                onChange={event => setName(event.target.value)}
                            />
                        </div>

                        <div className={styles.groupField}>
                            <label className={styles.required} htmlFor='surname'>
                                Sobrenome
                            </label>
                            <input
                                type='text'
                                id='surname'
                                name='surname'
                                value={surname}
                                onChange={event => setSurname(event.target.value)}
                            />
                        </div>

                        <div className={styles.groupField}>
                            <label className={styles.required}>Gênero</label>

                            <div className={`${styles.groupField} ${styles.option}`}>
                                <input
                                    type='radio'
                                    key='genderMale'
                                    id='genderMale'
                                    name='gender'
                                    value='M'
                                    onChange={event => setGender(event.target.value)}
                                />
                                <label htmlFor='genderMale'>Masculino</label>
                            </div>
                            <div className={`${styles.groupField} ${styles.option}`}>
                                <input
                                    type='radio'
                                    key='genderFemale'
                                    id='genderFemale'
                                    name='gender'
                                    value='F'
                                    onChange={event => setGender(event.target.value)}
                                />
                                <label htmlFor='genderFemale'>Feminino</label>
                            </div>
                            <div className={`${styles.groupField} ${styles.option}`}>
                                <input
                                    type='radio'
                                    key='genderNonBinary'
                                    id='genderNonBinary'
                                    name='gender'
                                    value='N'
                                    onChange={event => setGender(event.target.value)}
                                />
                                <label htmlFor='genderNonBinary'>Não binário</label>
                            </div>
                            <div className={`${styles.groupField} ${styles.option}`}>
                                <input
                                    type='radio'
                                    key='genderNotMention'
                                    id='genderNotMention'
                                    name='gender'
                                    value='NM'
                                    onChange={event => setGender(event.target.value)}
                                />
                                <label htmlFor='genderNotMention'>Prefiro não mencionar</label>
                            </div>
                        </div>

                        <div className={styles.groupField}>
                            <label className={styles.required} htmlFor='email'>
                                E-mail
                            </label>
                            <input
                                type='email'
                                id='email'
                                name='email'
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                            />
                        </div>

                        <div className={styles.groupField}>
                            <label className={styles.required} htmlFor='password'>
                                Senha
                            </label>
                            <div className={styles.passwordField}>
                                <input
                                    minLength={5}
                                    type={passwordType}
                                    id='password'
                                    name='password'
                                    value={password}
                                    onChange={event => setPassword(event.target.value)}
                                />
                                <div
                                    className={`fa ${
                                        passwordType === 'password' ? 'fa-eye-slash' : 'fa-eye'
                                    }`}
                                    onClick={togglePassword}
                                ></div>
                            </div>
                        </div>

                        <div className={styles.groupField}>
                            <label className={styles.required} htmlFor='passwordConfirmation'>
                                Confirme sua senha
                            </label>
                            <div className={styles.passwordField}>
                                <input
                                    minLength={5}
                                    type={passwordConfirmationType}
                                    id='passwordConfirmation'
                                    name='passwordConfirmation'
                                    value={passwordConfirmation}
                                    onChange={event => setPasswordConfirmation(event.target.value)}
                                />
                                <div
                                    className={`fa ${
                                        passwordConfirmationType === 'password'
                                            ? 'fa-eye-slash'
                                            : 'fa-eye'
                                    }`}
                                    onClick={togglePasswordConfirmation}
                                ></div>
                            </div>
                        </div>

                        <div className={`${styles.groupField} ${styles.imageUploadField}`}>
                            <label className={styles.required}>Imagem de perfil</label>

                            <div className={styles.imageButton}>
                                <label htmlFor='image' aria-hidden='true'>
                                    Selecione uma imagem
                                </label>
                                <input
                                    type='file'
                                    accept='image/*'
                                    id='image'
                                    name='image'
                                    onChange={handleImageChange}
                                />
                            </div>

                            {previewImageURL !== '' && (
                                <figure>
                                    <img src={previewImageURL} alt='Imagem de perfil do usuário' />
                                    <figcaption>{image && image.name}</figcaption>
                                </figure>
                            )}
                        </div>

                        <div className={`${styles.groupField} ${styles.lgpd} ${styles.option}`}>
                            <input
                                type='checkbox'
                                name='agreedLgpd'
                                id='agreedLgpd'
                                value={agreedLgpd}
                                onChange={handleAgreedLgpd}
                            />
                            <label htmlFor='agreedLgpd'>
                                Declaro que li e concordo com o{' '}
                                <a href='#' target='_blank' onClick={showPolicies}>
                                    Termo de Uso e Política de Privacidade
                                </a>{' '}
                                da plataforma. Além disso, confirmo que as informações fornecidas
                                neste formulário de cadastro são verdadeiras.
                            </label>
                        </div>

                        <button
                            type='submit'
                            className={`${styles.loginOption} ${styles.submitButton}`}
                        >
                            Finalizar cadastro
                        </button>

                        <div className={styles.links}>
                            <br />
                            <NavLink to='/auth/sign-in?open=true'>Já possui cadastro?</NavLink>
                        </div>
                    </form>
                </div>
            </LoginBox>
        </div>
    );
};
